import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, FileText, X } from "lucide-react";
import { Card, Button, Badge, Spinner } from "../../shared/ui";
import FormSignaturePad from "../FormSignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Acknowledgements Tab — supervisor-filled forms awaiting the worker's "I have read & understood" signature
const AcknowledgementsTab = ({ data, onSign }) => {
  const total = data?.total || 0;
  if (total === 0) {
    return (
      <Card className="text-center py-12 bg-slate-50" data-testid="acknowledgements-empty">
        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
        <p className="font-bold uppercase">All caught up!</p>
        <p className="text-sm text-slate-500 mt-1">No supervisor-filled forms waiting on your signature.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="acknowledgements-tab">
      <Card className="bg-amber-50 border-2 border-amber-300">
        <p className="text-sm">
          <strong className="uppercase text-amber-900">{total} form(s) need your acknowledgement.</strong>
          <span className="text-amber-700 block mt-1">
            These forms have already been filled out by your supervisor — read through, then tap "Sign to Acknowledge" to confirm you've understood.
          </span>
        </p>
      </Card>
      {data.allocations.map(a => (
        <Card key={a.id} interactive className="flex items-start gap-3" onClick={() => onSign(a)} data-testid={`ack-card-${a.id}`}>
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold">{a.template_name}</h4>
              {a.template_category && <Badge variant="info">{a.template_category}</Badge>}
              <Badge variant="warning">SIGN TO ACKNOWLEDGE</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Job: <strong>{a.job_title}</strong>
              {a.client_name && <span> · Client: {a.client_name}</span>}
            </p>
            <p className="text-xs text-slate-500">
              Filled by <strong>{a.primary_submitted_by || 'supervisor'}</strong>
              {a.primary_submitted_at && <span> · {new Date(a.primary_submitted_at).toLocaleString('en-AU')}</span>}
            </p>
          </div>
          <Button size="sm" data-testid={`open-ack-${a.id}`}>
            Read & Sign
          </Button>
        </Card>
      ))}
    </div>
  );
};

// Acknowledgement Sign Modal — read-only view of supervisor's submission + signature pad
const AcknowledgeSignModal = ({ allocation, onClose, onSigned }) => {
  const [signing, setSigning] = useState(false);
  const sub = allocation.primary_submission || {};
  const fields = sub.fields || sub.data || {};

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      await axios.post(`${API}/jobs/${allocation.job_id}/form-allocations/${allocation.id}/sign`, {
        signature_data: signatureData,
        submission_id: sub.id
      });
      toast.success('Signed — thanks for acknowledging!');
      onSigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-lg border-2 border-slate-200 shadow-xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="acknowledge-sign-modal"
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg sm:text-xl font-bold uppercase">{allocation.template_name}</h2>
            <p className="text-xs text-slate-500">Read & sign to acknowledge</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Header info */}
          <Card className="bg-blue-50 border border-blue-200">
            <div className="text-sm space-y-1">
              <div><strong>Job:</strong> {allocation.job_title}</div>
              {allocation.client_name && <div><strong>Client:</strong> {allocation.client_name}</div>}
              <div><strong>Filled by:</strong> {allocation.primary_submitted_by || 'supervisor'}</div>
              {allocation.primary_submitted_at && (
                <div><strong>Filled on:</strong> {new Date(allocation.primary_submitted_at).toLocaleString('en-AU')}</div>
              )}
            </div>
          </Card>

          {/* Read-only fields */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Form Contents (read-only)</h3>
            <Card className="space-y-2 bg-slate-50">
              {Object.keys(fields).length === 0 ? (
                <p className="text-sm text-slate-500 italic">No fields filled in.</p>
              ) : (
                Object.entries(fields).map(([k, v]) => (
                  <div key={k} className="text-sm border-b border-slate-200 pb-2 last:border-0 last:pb-0">
                    <p className="text-xs font-bold uppercase text-slate-500">{k.replace(/_/g, ' ')}</p>
                    <p className="text-slate-800 break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v || '—')}</p>
                  </div>
                ))
              )}
            </Card>
          </div>

          {/* Existing signatures (so worker sees who else has signed) */}
          {(allocation.crew_signatures || []).length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Already acknowledged by</h3>
              <div className="flex flex-wrap gap-2">
                {(allocation.crew_signatures || []).map(s => (
                  <Badge key={s.id} variant="success">{s.user_name}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Acknowledgement banner */}
          <Card className="bg-amber-50 border-2 border-amber-300 text-sm">
            <p className="font-bold text-amber-900">By signing below, I confirm that:</p>
            <ul className="list-disc pl-5 mt-1 text-amber-800 space-y-0.5">
              <li>I have read this {(allocation.template_category || 'form').toLowerCase()} in full.</li>
              <li>I understand the requirements and will follow them.</li>
              <li>I will raise any questions before starting work.</li>
            </ul>
          </Card>

          {/* Signature pad */}
          <div>
            <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Your signature</h3>
            <FormSignaturePad
              onSign={handleSign}
              onCancel={onClose}
            />
            {signing && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-2">
                <Spinner size="sm" /> Recording signature…
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AcknowledgementsTab, AcknowledgeSignModal };
